import { Txn, Group, Category } from '../money.model';
import { DonutSegment } from '../charts/donut-chart.component';

export interface MonthTotal { month: string; spentCents: number; }
export interface MonthKpis {
  spentCents: number;
  incomeCents: number;
  netCents: number;
  txnCount: number;
  prevSpentCents: number;
  /** Percent change in spend vs the previous month; null when there is no basis. */
  deltaPct: number | null;
}

/** Colour for spending that has not been categorized yet. */
export const UNCATEGORIZED_COLOR = '#c9bba6';

/**
 * Everything that counts toward the numbers: no transfers, no excluded rows.
 * 352 of the 738 real transactions are transfers — counting them makes every
 * figure meaningless.
 */
export function spendableTxns(txns: Txn[]): Txn[] {
  return (txns ?? []).filter(t => !t.isTransfer && !t.excluded);
}

export function monthKey(date: string): string { return (date ?? '').slice(0, 7); }

/** `count` months ending at (and including) `anchor`, oldest first. */
export function recentMonths(anchor: string, count: number): string[] {
  const [y, m] = anchor.split('-').map(Number);
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(y, m - 1 - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** Spend per month as a POSITIVE magnitude. Months with nothing report zero. */
export function monthlyTotals(txns: Txn[], months: string[]): MonthTotal[] {
  const spend = spendableTxns(txns).filter(t => t.amount < 0);
  return months.map(month => ({
    month,
    spentCents: spend
      .filter(t => monthKey(t.date) === month)
      .reduce((n, t) => n + Math.abs(t.amount), 0),
  }));
}

/**
 * Spending rolled up to groups, biggest first, ready for the donut.
 *
 * Uncategorized spending gets its own segment rather than being dropped — 386
 * of the seeded rows start uncategorized, and silently omitting them would make
 * the donut disagree with the headline total.
 *
 * No `isSpending` check here: this is already filtered to `amount < 0`, which
 * is what keeps income rows out — a positive paycheck never reaches this loop
 * regardless of its group. A negative row miscategorized under a non-spending
 * group (e.g. a refund filed under Income) still counts, and shows up as a
 * visibly wrong slice rather than vanishing from the total — the better
 * failure mode for a financial tool. A category whose group no longer exists
 * (orphaned) is routed to `uncategorized` rather than dropped, for the same
 * reason: it must still count toward the total the KPI card reports.
 */
export function groupMix(txns: Txn[], categories: Category[], groups: Group[]): DonutSegment[] {
  const catById = new Map(categories.map(c => [c.id, c]));
  const groupById = new Map(groups.map(g => [g.id, g]));
  const totals = new Map<string, number>();

  spendableTxns(txns).filter(t => t.amount < 0).forEach(t => {
    let key: string;
    if (!t.categoryId) {
      key = 'uncategorized';
    } else {
      const cat = catById.get(t.categoryId);
      const group = cat ? groupById.get(cat.groupId) : undefined;
      key = group ? group.id : 'uncategorized';
    }
    totals.set(key, (totals.get(key) ?? 0) + Math.abs(t.amount));
  });

  const segments: DonutSegment[] = [];
  totals.forEach((value, key) => {
    const group = groupById.get(key);
    segments.push({
      id: key,
      label: group ? group.name : 'Uncategorized',
      value,
      color: group ? group.color : UNCATEGORIZED_COLOR,
    });
  });

  return segments.sort((a, b) => b.value - a.value);
}

/**
 * How far a shade may travel toward white.
 *
 * The requirement this serves: the lightest slice must stay distinguishable
 * from the card it sits on, which is $warm-white #fff9f0. At 0.55 the
 * lightest tint still retains 45% of the base color's distance from white —
 * Essentials #7aab8a ends at #c3d9ca, clearly a green against a cream card.
 *
 * This is a visual judgement, not a figure fitted to any data, and it is
 * checked rather than asserted: see the 'stays distinguishable from the card
 * it sits on' spec, which fails if a future value lets the ramp wash out.
 */
const MAX_TINT = 0.55;

/** '#rrggbb' or '#rgb' as [r, g, b], or null when the string is not hex. */
function hexToRgb(hex: string): [number, number, number] | null {
  const raw = (hex ?? '').trim().replace('#', '');
  const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

const toHex = (n: number): string => n.toString(16).padStart(2, '0');

/**
 * `n` evenly spaced tints of one color, deepest first.
 *
 * Needed because categories carry no color of their own — `Category` is
 * `{id, name, groupId, order}` and nothing more — so a drilled donut has to
 * derive its slices from the parent group. Tints of the group's own color
 * rather than a fresh palette keep the drill visually anchored: the chart
 * still reads as "inside Essentials" rather than as an unrelated chart.
 *
 * A color this cannot parse is returned unchanged for every slice. Rendering
 * a group's slices all one color is a legible degradation; computing from a
 * failed parse would render them black.
 */
export function groupShades(baseColor: string, n: number): string[] {
  if (n <= 0) return [];
  if (n === 1) return [baseColor];

  const rgb = hexToRgb(baseColor);
  if (!rgb) return Array.from({ length: n }, () => baseColor);

  const [r, g, b] = rgb;
  return Array.from({ length: n }, (_, i) => {
    const t = (i / (n - 1)) * MAX_TINT;
    return '#'
      + toHex(Math.round(r + (255 - r) * t))
      + toHex(Math.round(g + (255 - g) * t))
      + toHex(Math.round(b + (255 - b) * t));
  });
}

/**
 * One group's spending broken into its categories, biggest first.
 *
 * Same DonutSegment shape as groupMix, so the donut component needs no new
 * inputs to render a drilled view — only different segments.
 *
 * Uncategorized rows are absent by construction: a row with no categoryId
 * belongs to no group, so there is nothing to attribute it to here. The
 * Uncategorized pseudo-group is handled by navigation instead (see the
 * dashboard component), because it has no categories to drill into.
 */
export function categoryMix(
  txns: Txn[], categories: Category[], groupId: string, baseColor: string,
): DonutSegment[] {
  const inGroup = new Map(
    (categories ?? []).filter(c => c.groupId === groupId).map(c => [c.id, c]));

  const totals = new Map<string, number>();
  spendableTxns(txns).filter(t => t.amount < 0).forEach(t => {
    if (!t.categoryId || !inGroup.has(t.categoryId)) return;
    totals.set(t.categoryId, (totals.get(t.categoryId) ?? 0) + Math.abs(t.amount));
  });

  const ranked = [...totals.entries()]
    .map(([id, value]) => ({ id, label: inGroup.get(id)!.name, value }))
    .sort((a, b) => b.value - a.value);

  const shades = groupShades(baseColor, ranked.length);
  return ranked.map((row, i) => ({ ...row, color: shades[i] }));
}

export function monthKpis(txns: Txn[], month: string): MonthKpis {
  const spendable = spendableTxns(txns);
  const inMonth = spendable.filter(t => monthKey(t.date) === month);

  const spentCents = inMonth.filter(t => t.amount < 0)
    .reduce((n, t) => n + Math.abs(t.amount), 0);
  const incomeCents = inMonth.filter(t => t.amount > 0)
    .reduce((n, t) => n + t.amount, 0);

  const [prev] = recentMonths(month, 2);
  const prevSpentCents = spendable
    .filter(t => t.amount < 0 && monthKey(t.date) === prev)
    .reduce((n, t) => n + Math.abs(t.amount), 0);

  return {
    spentCents,
    incomeCents,
    netCents: incomeCents - spentCents,
    txnCount: inMonth.length,
    prevSpentCents,
    deltaPct: prevSpentCents > 0
      ? ((spentCents - prevSpentCents) / prevSpentCents) * 100
      : null,
  };
}
